/**
 * Static assets for strategy stages — Rive paths and header/review icon SVGs.
 *
 * Belongs here: mapping API `stages[].id` → .riv path or icon markup.
 * Does not belong here: when to play Rives or React components.
 *
 * Rive lookup uses API ids directly (`life_stage`, etc.). `StrategyStageKind` is UI-only for icons.
 */
import { StrategyQuestionnaireStageId } from '../../shared/types/questionnaire.types'

/** UI-only — not an API field */
export enum StrategyStageKind {
  Life = 'life',
  Risk = 'risk',
  Sip = 'sip',
  Unknown = 'unknown'
}

const DEFAULT_STAGE_KIND = StrategyStageKind.Life

export function getStrategyStageKind(stageId: string | undefined): StrategyStageKind {
  if (!stageId) return StrategyStageKind.Unknown
  if (stageId === StrategyQuestionnaireStageId.LifeStage) return StrategyStageKind.Life
  if (stageId === StrategyQuestionnaireStageId.RiskAssessment) return StrategyStageKind.Risk
  if (stageId === StrategyQuestionnaireStageId.SipAllocation) return StrategyStageKind.Sip
  if (/sip/i.test(stageId)) return StrategyStageKind.Sip
  if (/risk/i.test(stageId)) return StrategyStageKind.Risk
  if (/life/i.test(stageId)) return StrategyStageKind.Life
  return StrategyStageKind.Unknown
}

function resolveStageKind(stageId: string | undefined): Exclude<StrategyStageKind, StrategyStageKind.Unknown> {
  const kind = getStrategyStageKind(stageId)
  if (kind === StrategyStageKind.Unknown) return DEFAULT_STAGE_KIND
  return kind
}

// ─── Rives (keyed by API stage.id) ───────────────────────────────────────────

const STAGE_INTRO_RIVE_BY_STAGE_ID: Record<string, string> = {
  [StrategyQuestionnaireStageId.LifeStage]: '/rives/allocation/life_stage.riv',
  [StrategyQuestionnaireStageId.RiskAssessment]: '/rives/allocation/risk_stage.riv',
  [StrategyQuestionnaireStageId.SipAllocation]: '/rives/allocation/sip_stage.riv'
}

export const STAGE_COMPLETION_RIVE_SRC = '/rives/allocation/completed.riv'

/**
 * @param stageId — API `stages[].id` (StrategyQuestionnaireStageId value)
 * @returns public path to intro .riv, or undefined if unmapped
 */
export function getStageIntroRiveSrc(stageId: string | undefined): string | undefined {
  if (!stageId) return undefined
  if (stageId in STAGE_INTRO_RIVE_BY_STAGE_ID) {
    return STAGE_INTRO_RIVE_BY_STAGE_ID[stageId]
  }
  const kind = getStrategyStageKind(stageId)
  if (kind === StrategyStageKind.Unknown) return undefined
  const byKind: Record<Exclude<StrategyStageKind, StrategyStageKind.Unknown>, string> = {
    [StrategyStageKind.Life]: STAGE_INTRO_RIVE_BY_STAGE_ID[StrategyQuestionnaireStageId.LifeStage],
    [StrategyStageKind.Risk]: STAGE_INTRO_RIVE_BY_STAGE_ID[StrategyQuestionnaireStageId.RiskAssessment],
    [StrategyStageKind.Sip]: STAGE_INTRO_RIVE_BY_STAGE_ID[StrategyQuestionnaireStageId.SipAllocation]
  }
  return byKind[kind]
}

// ─── Icons ───────────────────────────────────────────────────────────────────

export type StrategyStageIconVariant = 'header' | 'review'

const STAGE_ICON_BY_KIND: Record<
  Exclude<StrategyStageKind, StrategyStageKind.Unknown>,
  Record<StrategyStageIconVariant, string>
> = {
  [StrategyStageKind.Life]: {
    header: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M6.11523 10V12.5893" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.23145 17.77H17.7672" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.23145 14.5312H17.7672" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.51815 4.72066C8.3713 3.32321 7.15593 2.23291 5.67835 2.23291C4.10175 2.23291 2.82367 3.47424 2.82367 5.0055C2.82367 5.52968 2.97343 6.01987 3.23365 6.43792C2.63562 6.76356 2.23145 7.38515 2.23145 8.09842C2.23145 9.14905 3.10836 10.0008 4.1901 10.0008C5.26914 10.0008 5.96772 10.0008 7.09512 10.0008C8.69905 10.0008 9.9993 8.73791 9.9993 7.18009C9.9993 6.12443 9.40221 5.20422 8.51815 4.72066Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.9414 2.23291C13.236 2.23291 13.8834 3.52755 13.8834 3.52755C13.8834 3.52755 14.5307 2.23291 15.8253 2.23291" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M13.8838 6.11621C15.1784 6.11621 15.8258 7.41085 15.8258 7.41085C15.8258 7.41085 16.4731 6.11621 17.7677 6.11621" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    review: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <rect width="24" height="24" fill="white" fill-opacity="0.0901961"/>
  <path d="M7.33789 12V15.1071" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.67773 21.3242H21.3206" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.67773 17.4375H21.3206" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M10.2218 5.66498C10.0456 3.98804 8.58712 2.67969 6.81403 2.67969C4.9221 2.67969 3.3884 4.16928 3.3884 6.0068C3.3884 6.63581 3.56812 7.22404 3.88038 7.7257C3.16274 8.11647 2.67773 8.86237 2.67773 9.7183C2.67773 10.9791 3.73003 12.0011 5.02812 12.0011C6.32297 12.0011 7.16126 12.0011 8.51414 12.0011C10.4389 12.0011 11.9992 10.4857 11.9992 8.6163C11.9992 7.34951 11.2827 6.24526 10.2218 5.66498Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M14.3301 2.67969C15.8836 2.67969 16.6604 4.23326 16.6604 4.23326C16.6604 4.23326 17.4372 2.67969 18.9908 2.67969" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16.6602 7.33984C18.2137 7.33984 18.9905 8.89341 18.9905 8.89341C18.9905 8.89341 19.7673 7.33984 21.3209 7.33984" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
  },
  [StrategyStageKind.Risk]: {
    header: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_65664_1429)">
    <path d="M13.347 17.3682C15.566 17.3682 17.3648 15.5694 17.3648 13.3504C17.3648 11.1314 15.566 9.33252 13.347 9.33252C11.128 9.33252 9.3291 11.1314 9.3291 13.3504C9.3291 15.5694 11.128 17.3682 13.347 17.3682Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M18.7046 18.7075L16.1885 16.1914" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M18.7047 1.29541L15.3564 6.65255" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M1.29395 14.6889L4.64216 7.99247L6.65109 10.671L9.9993 3.97461L12.0082 6.65314" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_65664_1429">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>`,
    review: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <g clip-path="url(#clip0_97300_139365)">
    <rect width="24" height="24" fill="white" fill-opacity="0.0901961"/>
    <path d="M16.0167 20.8421C18.6796 20.8421 20.8382 18.6835 20.8382 16.0206C20.8382 13.3579 18.6796 11.1992 16.0167 11.1992C13.3539 11.1992 11.1953 13.3579 11.1953 16.0206C11.1953 18.6835 13.3539 20.8421 16.0167 20.8421Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M22.4451 22.449L19.4258 19.4297" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M22.4456 1.55469L18.4277 7.98326" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M1.55273 17.6267L5.57059 9.59096L7.98131 12.8052L11.9992 4.76953L14.4099 7.98377" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_97300_139365">
      <rect width="24" height="24" fill="white"/>
    </clipPath>
  </defs>
</svg>`
  },
  [StrategyStageKind.Sip]: {
    header: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M5.98145 9.33008H14.0172" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M5.98145 13.3477H9.9993" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M3.30315 3.97266C2.94795 3.97266 2.6073 4.11376 2.35613 4.36492C2.10497 4.61609 1.96387 4.95674 1.96387 5.31194V16.6959C1.96387 17.051 2.10497 17.3918 2.35613 17.6429C2.6073 17.894 2.94795 18.0352 3.30315 18.0352H16.696C17.0512 18.0352 17.3919 17.894 17.643 17.6429C17.8941 17.3918 18.0353 17.051 18.0353 16.6959V5.31194C18.0353 4.95674 17.8941 4.61609 17.643 4.36492C17.3919 4.11376 17.0512 3.97266 16.696 3.97266H3.30315Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M5.3125 1.96387V5.98172" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M14.6875 1.96387V5.98172" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
    review: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M7.17871 11.1965H16.8216" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.17871 16.0178H12.0001" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M3.96456 4.76782C3.53832 4.76782 3.12954 4.93715 2.82814 5.23854C2.52675 5.53994 2.35742 5.94872 2.35742 6.37497V20.0357C2.35742 20.4619 2.52675 20.8708 2.82814 21.1721C3.12954 21.4734 3.53832 21.6428 3.96456 21.6428H20.036C20.4622 21.6428 20.8711 21.4734 21.1724 21.1721C21.4737 20.8708 21.6431 20.4619 21.6431 20.0357V6.37497C21.6431 5.94872 21.4737 5.53994 21.1724 5.23854C20.8711 4.93715 20.4622 4.76782 20.036 4.76782H3.96456Z" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M6.375 2.35718V7.17861" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M17.625 2.35718V7.17861" stroke="#232323" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
  }
}

export const STAGE_ICON_COMPLETED_HEADER = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect width="20" height="20" fill="white" fill-opacity="0.0901961"/>
  <path d="M4 12L8.01646 16L16 4" stroke="#00661D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

export function getStageIconSvg(stageId: string | undefined, variant: StrategyStageIconVariant): string {
  const kind = resolveStageKind(stageId)
  return STAGE_ICON_BY_KIND[kind][variant]
}
